/**
 * Стадия заказа → шаг трекера (FE-192, MVP_PRD §33).
 *
 * Стадия — состояние backend, шаг — то, что видит пользователь; несколько
 * стадий отображаются одним шагом. Соответствие живёт **здесь и только здесь**:
 * страница не знает о шагах и не рисует навигацию (DESIGN_SYSTEM §4.3).
 *
 * Функции чистые: ни запросов, ни i18n, ни React. Подписи шагов приходят из
 * локалей по коду (`app.tracker.step.<key>`), потому что LLM и backend
 * пользовательских строк не пишут.
 */

/**
 * Стадии заказа (MVP_PRD §33.2). Порядок — порядок таблицы в документе.
 *
 * Список зеркалит `OrderStage` backend (BE-200) и приходит с сервера полем
 * `search_sessions.stage`. Стадии `round_exhausted` в нём **нет**: раундов по
 * четыре не существует, вариант в четвёрке заменяется поштучно
 * (MVP_TECH_RFC §34.13).
 */
export const ORDER_STAGES = [
  'draft',
  'ready',
  'analyzing',
  'analysis_failed',
  'shortlist_ready',
  'awaiting_registration',
  'awaiting_payment',
  'paid',
  'selecting',
  'top4_ready',
  'checking',
  'comparison_ready',
  'deep_check',
  'deep_check_ready',
  'negotiating',
  'offer_review',
  'conclusion_ready',
  'inspection',
  'inspection_review',
  'renegotiating',
  'handover',
  'completed',
  'closed',
  'stale',
  'archived',
] as const;

export type OrderStage = (typeof ORDER_STAGES)[number];

/** Номер шага трекера. Семь шагов, `null` — заказ вне воронки. */
export type StepNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface TrackerStep {
  number: StepNumber;
  /** Ключ i18n без префикса: `app.tracker.step.<key>`. */
  key: string;
}

/** Семь шагов воронки (MVP_PRD §33.1). Оплата стоит третьей, а не шестой. */
export const TRACKER_STEPS: readonly TrackerStep[] = [
  { number: 1, key: 'search' },
  { number: 2, key: 'analysis' },
  { number: 3, key: 'payment' },
  { number: 4, key: 'selection' },
  { number: 5, key: 'negotiation' },
  { number: 6, key: 'results' },
  { number: 7, key: 'completion' },
];

export const TOTAL_STEPS = TRACKER_STEPS.length;

/**
 * Соответствие стадии и шага (MVP_PRD §33.2).
 *
 * `null` — заказ вне воронки: `closed` и `archived` не стоят ни на одном шаге,
 * и подсвечивать им шаг было бы враньём о состоянии.
 *
 * Четыре строки таблицы, которые нельзя «поправить по логике»:
 *
 * * `checking` и `comparison_ready` — шаг **4**, а не 5: контрольная проверка
 *   у четырёх автохаусов и сравнение идут до выбора автомобиля;
 * * `deep_check` и `deep_check_ready` — тоже шаг **4** (MVP_PRD §33.2, редакция
 *   от 05.09.2026). Углублённая проверка идёт по **всем четырём** автомобилям и
 *   предшествует выбору: она часть проверки, а не торга. Раньше `deep_check`
 *   стоял на шаге 5, потому что запускался по одному уже выбранному автомобилю;
 * * `inspection`, `inspection_review` и `renegotiating` — шаг **7**: осмотр,
 *   пробная поездка и второй раунд торга идут после согласия с предложением;
 * * `stale` — шаг **2**: каталог обновился, результаты пересчитываются заново.
 */
const STAGE_TO_STEP: Record<OrderStage, StepNumber | null> = {
  draft: 1,
  ready: 1,
  analyzing: 2,
  analysis_failed: 2,
  shortlist_ready: 2,
  awaiting_registration: 3,
  awaiting_payment: 3,
  paid: 3,
  selecting: 4,
  top4_ready: 4,
  checking: 4,
  comparison_ready: 4,
  deep_check: 4,
  deep_check_ready: 4,
  negotiating: 5,
  offer_review: 6,
  conclusion_ready: 6,
  inspection: 7,
  inspection_review: 7,
  renegotiating: 7,
  handover: 7,
  completed: 7,
  closed: null,
  stale: 2,
  archived: null,
};

/** Шаг, на котором стоит заказ. `null` — заказ вне воронки (закрыт, в архиве). */
export function stageToStep(stage: OrderStage): StepNumber | null {
  return STAGE_TO_STEP[stage];
}

/** Состояние шага в трекере (DESIGN_SYSTEM §5.3). */
export type StepState = 'done' | 'current' | 'upcoming' | 'locked';

export interface TrackerStepState extends TrackerStep {
  state: StepState;
  /** Пройденный шаг кликабелен, будущий и закрытый — нет: это индикатор, не меню. */
  navigable: boolean;
}

export interface TrackerInput {
  stage: OrderStage;
  /**
   * Оплачен ли заказ. Отдельно от стадии: после правки критериев оплаченный
   * заказ возвращается на стадию `analyzing`, но замки с шагов 4–7 не
   * возвращаются — платить второй раз не нужно (MVP_PRD §35.4).
   */
  paid: boolean;
}

/** Первый шаг, закрытый оплатой. Шаги 4–7 идут после платежа (MVP_PRD §33.1). */
const FIRST_PAID_STEP = 4;

/**
 * Состояния всех семи шагов.
 *
 * Шаг, недоступный из-за оплаты, **показывается с замком, а не скрывается**:
 * пользователь должен видеть, что дальше, и почему туда пока нельзя (§33.3).
 * Если заказ вне воронки (`closed`, `archived`), текущего шага нет и все
 * шаги — будущие: подсветка чужого состояния хуже её отсутствия.
 */
export function trackerSteps({ stage, paid }: TrackerInput): TrackerStepState[] {
  const current = stageToStep(stage);

  return TRACKER_STEPS.map((step) => {
    const locked = !paid && step.number >= FIRST_PAID_STEP;

    if (current === null) {
      return { ...step, state: locked ? 'locked' : 'upcoming', navigable: false };
    }
    if (step.number < current) {
      return { ...step, state: 'done', navigable: true };
    }
    if (step.number === current) {
      return { ...step, state: 'current', navigable: true };
    }
    return { ...step, state: locked ? 'locked' : 'upcoming', navigable: false };
  });
}
